import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Table, TableHeaderRow, TableHeaderCell, TableRow, TableCell, Dialog, Bar, Input, Label, Select, Option, Switch, MessageStrip, FlexBox, BusyIndicator, Text, Tag } from '@ui5/webcomponents-react';
import { api, type Tenant, type DiscoveredCompany, type ProvisionSummary } from '../../api/client';
import { PageHeader } from '../../components/Responsive';

/**
 * Companies (multi-tenant admin, superuser). Add a SAP company database into B1Links from the app —
 * discovered from SAP via the SLD when configured, else entered by name — provisioned live (UDTs,
 * ADMIN, screens, KPIs) with no CLI, no .env edit, no restart. Enable/disable/remove existing ones.
 */
export function CompaniesAdmin() {
  const qc = useQueryClient();
  const tenants = useQuery({ queryKey: ['admin-tenants'], queryFn: api.adminTenants });
  const [adding, setAdding] = useState(false);

  const refresh = () => { qc.invalidateQueries({ queryKey: ['admin-tenants'] }); qc.invalidateQueries({ queryKey: ['companies'] }); };

  const toggle = async (t: Tenant) => {
    try { await api.updateTenant(t.db, { active: !t.active }); refresh(); }
    catch (e) { alert((e as Error).message); }
  };
  const remove = async (t: Tenant) => {
    if (!window.confirm(`Remove ${t.db} from B1Links? This unlists it from the login picker. SAP data is NOT deleted.`)) return;
    try { await api.removeTenant(t.db); refresh(); }
    catch (e) { alert((e as Error).message); }
  };

  const list = tenants.data?.tenants ?? [];

  return (
    <div>
      <PageHeader title="Companies" level="H4" style={{ marginBottom: '0.5rem' }} actions={<Button design="Emphasized" icon="add" onClick={() => setAdding(true)}>Add company</Button>} />
      <Text style={{ display: 'block', marginBottom: '0.75rem', color: 'var(--sapNeutralTextColor)' }}>
        Each company is a separate SAP Business One database. Users pick their company at login; every company keeps its own
        users, screens, and data. The control company cannot be removed.
      </Text>

      {tenants.isLoading ? <BusyIndicator active /> : (
        <Table overflowMode="Popin" headerRow={<TableHeaderRow>
          <TableHeaderCell minWidth="8rem" importance={2}><span>Database</span></TableHeaderCell>
          <TableHeaderCell minWidth="9rem"><span>Name</span></TableHeaderCell>
          <TableHeaderCell minWidth="5rem"><span>Status</span></TableHeaderCell>
          <TableHeaderCell minWidth="10rem"><span>Provisioned</span></TableHeaderCell>
          <TableHeaderCell minWidth="7rem" importance={1}><span></span></TableHeaderCell>
        </TableHeaderRow>}>
          {list.map(t => (
            <TableRow key={t.db}>
              <TableCell><span>{t.db}</span></TableCell>
              <TableCell><Text>{t.name}</Text></TableCell>
              <TableCell>
                {t.isControl ? <Tag colorScheme="6">Control</Tag> : <Tag colorScheme={t.active ? '8' : '2'}>{t.active ? 'Active' : 'Inactive'}</Tag>}
              </TableCell>
              <TableCell><Text style={{ color: 'var(--sapNeutralTextColor)' }}>{t.provisionedAt ? `${t.provisionedAt.slice(0, 10)} · ${t.provisionedBy ?? ''}` : '—'}</Text></TableCell>
              <TableCell>
                <FlexBox style={{ gap: '0.5rem' }} alignItems="Center">
                  {!t.isControl && <Switch checked={t.active} onChange={() => toggle(t)} />}
                  {!t.isControl && <Button design="Transparent" icon="delete" onClick={() => remove(t)} />}
                </FlexBox>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      )}

      {adding && (
        <AddCompanyDialog
          sldConfigured={tenants.data?.sldConfigured ?? false}
          onClose={() => setAdding(false)}
          onDone={() => { setAdding(false); refresh(); }}
        />
      )}
    </div>
  );
}

function AddCompanyDialog({ sldConfigured, onClose, onDone }: { sldConfigured: boolean; onClose: () => void; onDone: () => void }) {
  const discover = useQuery({
    queryKey: ['discover-tenants'], queryFn: api.discoverTenants, enabled: sldConfigured,
  });
  const [companyDb, setCompanyDb] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ProvisionSummary | null>(null);

  const available: DiscoveredCompany[] = (discover.data?.companies ?? []).filter(c => !c.registered);
  // SLD works only when configured AND it actually returned companies; otherwise fall back to typing.
  const useDropdown = sldConfigured && !discover.data?.error && available.length > 0;

  const submit = async () => {
    if (!companyDb) return;
    setBusy(true); setError(null);
    try {
      const r = await api.provisionTenant({ companyDb, name: name || undefined });
      setSummary(r.summary);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open headerText="Add company" onClose={onClose} style={{ width: 'min(560px, 96vw)' }}
      footer={
        <Bar endContent={
          summary
            ? <Button design="Emphasized" onClick={onDone}>Done</Button>
            : <><Button onClick={onClose} disabled={busy}>Cancel</Button><Button design="Emphasized" onClick={submit} disabled={busy || !companyDb}>{busy ? 'Provisioning…' : 'Provision'}</Button></>
        } />
      }>
      <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {summary ? (
          <>
            <MessageStrip design="Positive" hideCloseButton>Company <b>{companyDb}</b> is ready and now appears at login.</MessageStrip>
            <Text>Screens: {summary.screens.created} created, {summary.screens.skipped} already present.</Text>
            <Text>KPI tiles: {summary.kpis.created} created, {summary.kpis.skipped} already present.</Text>
            <Text>{summary.adminCreated ? 'Default admin created (ADMIN / admin123 — change it).' : 'Existing admin kept.'}</Text>
          </>
        ) : (
          <>
            {sldConfigured
              ? (discover.isLoading ? <BusyIndicator active /> :
                 discover.data?.error ? <MessageStrip design="Critical" hideCloseButton>Couldn’t list companies from SAP ({discover.data.error}). Enter the database name manually.</MessageStrip>
                 : available.length === 0 ? <MessageStrip design="Information" hideCloseButton>All discovered companies are already added. Enter a database name to add another.</MessageStrip>
                 : <MessageStrip design="Information" hideCloseButton>Pick a company discovered on your SAP server.</MessageStrip>)
              : <MessageStrip design="Information" hideCloseButton>SLD auto-list isn’t configured, so enter the SAP company database name. It’s validated before provisioning.</MessageStrip>}

            <Label required>Company database</Label>
            {useDropdown ? (
              <Select style={{ width: '100%' }} onChange={e => { const o = e.detail.selectedOption; setCompanyDb(o.dataset.db ?? ''); if (!name) setName(o.textContent ?? ''); }}>
                <Option data-db="">— select —</Option>
                {available.map(c => <Option key={c.db} data-db={c.db}>{c.db === c.name ? c.db : `${c.db} — ${c.name}`}</Option>)}
              </Select>
            ) : (
              <Input value={companyDb} placeholder="e.g. SBODemoUS" onInput={e => setCompanyDb(e.target.value ?? '')} style={{ width: '100%' }} />
            )}

            <Label>Display name (optional)</Label>
            <Input value={name} placeholder="Shown in admin lists" onInput={e => setName(e.target.value ?? '')} style={{ width: '100%' }} />

            {busy && <MessageStrip design="Information" hideCloseButton>Provisioning {companyDb} — creating tables, importing screens and KPIs. This can take up to a minute for a new database.</MessageStrip>}
            {error && <MessageStrip design="Negative" hideCloseButton>{error}</MessageStrip>}
          </>
        )}
      </div>
    </Dialog>
  );
}
